/**
 * Gráfico de evolución: precio promedio por día con la banda mínimo–máximo.
 *
 * SVG sin dependencias (el panel no trae librería de gráficos): se dibuja una
 * banda suave con el rango de cada día, la línea del promedio y un punto por
 * fecha con su `title` para ver el valor exacto al pasar el ratón.
 */

import { useId } from 'react';

import { formatCurrency } from '@/lib/utils';

import { diaMes } from '../lib/periodos';

export interface PuntoEvolucion {
  fecha: string;
  promedio: number;
  minimo: number;
  maximo: number;
  lecturas?: number;
}

const ANCHO = 720;
const ALTO = 240;
const MARGEN = { arriba: 16, derecha: 16, abajo: 30, izquierda: 66 };
const GUÍAS = 4;

/** Índices repartidos para no amontonar las etiquetas del eje X. */
function indicesEtiquetas(total: number, maximo: number): number[] {
  if (total <= maximo) return Array.from({ length: total }, (_, indice) => indice);

  const paso = (total - 1) / (maximo - 1);

  return Array.from({ length: maximo }, (_, indice) => Math.round(indice * paso));
}

export function GraficoEvolucion({ datos }: { datos: PuntoEvolucion[] }) {
  const idGradiente = `evolucion-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  if (datos.length === 0) {
    return (
      <p className="rounded-control border border-dashed border-ink-200 px-3 py-10 text-center text-sm text-ink-400">
        Sin lecturas en este periodo.
      </p>
    );
  }

  if (datos.length === 1) {
    const punto = datos[0];

    if (punto) {
      return (
        <div className="flex flex-col items-center gap-1 rounded-control border border-dashed border-ink-200 px-3 py-10 text-center">
          <span className="text-2xl font-bold tabular-nums text-ink-900">
            {formatCurrency(punto.promedio)}
          </span>
          <span className="text-xs text-ink-500">
            Única lectura del {diaMes(punto.fecha)} · mínimo {formatCurrency(punto.minimo)} · máximo{' '}
            {formatCurrency(punto.maximo)}
          </span>
          <span className="text-[11px] text-ink-400">
            Con dos días o más se dibuja la evolución.
          </span>
        </div>
      );
    }
  }

  const anchoUtil = ANCHO - MARGEN.izquierda - MARGEN.derecha;
  const altoUtil = ALTO - MARGEN.arriba - MARGEN.abajo;

  const valores = datos.flatMap((punto) => [punto.minimo, punto.maximo]);
  const brutoMin = Math.min(...valores);
  const brutoMax = Math.max(...valores);
  const holgura = Math.max((brutoMax - brutoMin) * 0.12, 0.2);
  const minimo = brutoMin - holgura;
  const maximo = brutoMax + holgura;

  const x = (indice: number) => MARGEN.izquierda + (indice / (datos.length - 1)) * anchoUtil;
  const y = (valor: number) =>
    MARGEN.arriba + ((maximo - valor) / (maximo - minimo)) * altoUtil;

  const linea = datos
    .map((punto, indice) => `${indice === 0 ? 'M' : 'L'} ${x(indice).toFixed(1)} ${y(punto.promedio).toFixed(1)}`)
    .join(' ');

  const banda = [
    ...datos.map(
      (punto, indice) => `${indice === 0 ? 'M' : 'L'} ${x(indice).toFixed(1)} ${y(punto.maximo).toFixed(1)}`,
    ),
    ...[...datos]
      .reverse()
      .map(
        (punto, indice) =>
          `L ${x(datos.length - 1 - indice).toFixed(1)} ${y(punto.minimo).toFixed(1)}`,
      ),
    'Z',
  ].join(' ');

  const guias = Array.from({ length: GUÍAS + 1 }, (_, indice) => {
    const valor = minimo + ((maximo - minimo) * indice) / GUÍAS;

    return { valor, y: y(valor) };
  });

  const etiquetas = indicesEtiquetas(datos.length, 6);

  return (
    <div className="space-y-2">
      <svg
        viewBox={`0 0 ${ANCHO} ${ALTO}`}
        className="h-auto w-full"
        role="img"
        aria-label={`Evolución del precio promedio entre el ${diaMes(
          datos[0]?.fecha ?? '',
        )} y el ${diaMes(datos[datos.length - 1]?.fecha ?? '')}`}
      >
        <defs>
          <linearGradient id={idGradiente} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {guias.map((guia) => (
          <g key={guia.valor}>
            <line
              x1={MARGEN.izquierda}
              x2={ANCHO - MARGEN.derecha}
              y1={guia.y}
              y2={guia.y}
              stroke="#e2e8f0"
              strokeWidth={1}
              strokeDasharray={guia.y === MARGEN.arriba ? undefined : '4 4'}
            />
            <text
              x={MARGEN.izquierda - 8}
              y={guia.y + 3.5}
              textAnchor="end"
              fontSize={10}
              fill="#94a3b8"
            >
              S/ {guia.valor.toFixed(1)}
            </text>
          </g>
        ))}

        <path d={banda} fill={`url(#${idGradiente})`} />

        <path
          d={linea}
          fill="none"
          stroke="#0891b2"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {datos.map((punto, indice) => (
          <g key={punto.fecha}>
            <circle cx={x(indice)} cy={y(punto.promedio)} r={3} fill="#0891b2" />
            <circle cx={x(indice)} cy={y(punto.promedio)} r={10} fill="transparent">
              <title>
                {`${diaMes(punto.fecha)}: ${formatCurrency(punto.promedio)}` +
                  ` (mín ${formatCurrency(punto.minimo)} · máx ${formatCurrency(punto.maximo)})`}
              </title>
            </circle>
          </g>
        ))}

        {etiquetas.map((indice) => (
          <text
            key={datos[indice]?.fecha ?? indice}
            x={x(indice)}
            y={ALTO - 8}
            textAnchor="middle"
            fontSize={10}
            fill="#94a3b8"
          >
            {diaMes(datos[indice]?.fecha ?? '')}
          </text>
        ))}
      </svg>

      <div className="flex flex-wrap items-center gap-4 text-[11px] text-ink-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-5 rounded-full bg-brand-600" aria-hidden="true" />
          Promedio del día
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-5 rounded-sm bg-brand-100" aria-hidden="true" />
          Rango mínimo–máximo
        </span>
      </div>
    </div>
  );
}
